<script lang="ts">
  import { browser, dev } from '$app/environment';
  import { beforeNavigate } from '$app/navigation';
  import type { GetCartQuery } from '$gql/storefront/operations';
  import { elevarAnalyticsEvent, ElevarAnalyticsEvents, type ElevarProduct } from '$lib/analytics';
  import { cart } from '$lib/cart';
  import { getProductCategory } from '$lib/cart/cart';
  import { DEFAULT_VENDOR, GUEST } from '$lib/constants';
  import { lang } from '$lib/lang';
  import { defaultCurrency, region } from '$lib/region';
  import { getFullURL } from '$lib/utils/paths';
  import { trimId, trimVariantId } from '$lib/utils/shopify/productsId';
  import { onMount } from 'svelte';
  import type { Unsubscriber } from 'svelte/store';

  const configPaths = {
    eu: 'configs/d4316012bd4b5258220082d45fdbf8fa3819248b/config.js',
    uk: 'configs/bb19a374e62fbe8146a3c33f6cef0a239f73e896/config.js',
    us: 'configs/daad0e3164dcabf6e25859f9328b2035625d89fe/config.js'
  };

  ///  <-- TODO: removed while Harper is testing acceptance of cookies events -->
  // eslint-disable-next-line svelte/valid-compile
  export const showCookies: (string | number)[] = [];

  // load the user data
  const loadUserData = (cartData: GetCartQuery['cart'] | null) => {
    const cartItems = cartData?.lines.nodes.filter((line) => line.quantity > 0) ?? [];

    const products: ElevarProduct[] = cartItems.map((line, index) => {
      const variantId = trimVariantId(line.merchandise.id);
      const collectionName =
        getProductCategory(line.merchandise.product.title, line.merchandise.product.productType) ||
        '';

      return {
        id: line.merchandise.sku,
        name: line.merchandise.product.title,
        brand: line.merchandise.product.vendor || DEFAULT_VENDOR,
        category: line.merchandise.product.productType,
        variant: line.merchandise.product.title,
        price: `${line.cost.totalAmount.amount}`,
        quantity: `${line.quantity}`,
        list: `/collections/${collectionName}`,
        product_id: trimId(line.merchandise.product.id),
        variant_id: variantId,
        compare_at_price: `${line.merchandise.compareAtPrice?.amount}` || '0.0',
        image: line.merchandise.image?.url,
        url: getFullURL({
          path: `/products/${line.merchandise.product.handle}`,
          region: $region,
          lang: $lang,
          searchQuery: `variant=${variantId}` || ''
        }),
        position: index + 1
      };
    });

    elevarAnalyticsEvent({
      event: ElevarAnalyticsEvents.USER_DATA,
      cart_total: `${cartData?.cost?.totalAmount?.amount || 0.0}`,
      user_properties: {
        visitor_type: GUEST
      },
      ecommerce: {
        currencyCode: cartData?.cost?.totalAmount?.currencyCode || defaultCurrency,
        cart_contents: {
          products
        }
      }
    });
  };

  function subscribeToCart(callback: (value: GetCartQuery['cart'] | null) => void): Unsubscriber {
    const unsubscribe: Unsubscriber = cart.subscribe((value) => {
      if (value !== undefined) {
        callback(value);
        // unsubscribe();
        setTimeout(() => {
          unsubscribe();
        }, 0);
      }
    });
    return unsubscribe;
  }

  beforeNavigate(() => {
    window.ElevarInvalidateContext?.();
    if ($cart) {
      loadUserData($cart);
    } else {
      // Fallback in case $cart is not populated
      subscribeToCart(loadUserData);
    }
  });

  onMount(async () => {
    if (dev) return;

    // Load and execute the Elevar script based on the current region
    try {
      const settings: { proxyPath: string } = { proxyPath: '/elevar' };
      const configPath = configPaths[$region];
      // Use the proxy path for the config import based on region
      const configModule = await import(
        /* @vite-ignore */ `${settings.proxyPath}/static/${configPath}`
      );
      const config = configModule.default;

      // Use the correct script URL based on settings.proxyPath
      let scriptUrl = settings.proxyPath
        ? `${settings.proxyPath}${config.script_src_custom_pages_proxied}`
        : config.script_src_custom_pages;

      // Dynamically load and execute the script if scriptUrl is valid
      // Try loading via proxy path; if it fails, fallback to direct URL
      try {
        const scriptModule = await import(/* @vite-ignore */ scriptUrl);
        const { handler } = scriptModule;
        await handler(config, settings);
      } catch (proxyError) {
        console.warn('Proxy path failed, attempting direct script load:', proxyError);
        scriptUrl = config.script_src_custom_pages; // Direct URL
        const scriptModule = await import(/* @vite-ignore */ scriptUrl);
        const { handler } = scriptModule;
        await handler(config, settings);
      }

      window.ElevarDebugMode?.(true);
    } catch (error) {
      console.error('Error loading Elevar script:', error);
    } finally {
      // Wait for the cart data to load
      subscribeToCart(loadUserData);
    }
  });
</script>

<svelte:head>
  {#if !dev}
    <script>
      (function (w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', 'GTM-PWWWTWCB');
    </script>
    {#if browser}
      <script>
        window.ElevarDataLayer = window.ElevarDataLayer ?? [];
      </script>
    {/if}
  {/if}

  <!-- TODO: removed while Harper is testing acceptance of cookies events -->
  <!-- {#if browser && showCookies.includes($region)}
    <script>
      // set default data for the gtag consent
      window.dataLayer = window.dataLayer || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      function gtag() {
        window.dataLayer.push(arguments);
      }

      window.gtag = gtag;

      if (localStorage.getItem('cookies_consent') === null) {
        gtag('consent', 'default', {
          ad_storage: 'denied', // google ads
          analytics_storage: 'denied', // google analytics
          personalization_storage: 'denied', // for personalized advertising.
          functionality_storage: 'denied', // languages
          security_storage: 'denied' // authorization or authentication
        });
      } else {
        gtag('consent', 'default', JSON.parse(localStorage.getItem('cookies_consent')));
      }
    </script>
  {/if}

  {#if browser && !showCookies.includes($region)}
    <script>
      // set default data for the gtag consent
      window.dataLayer = window.dataLayer || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      function gtag() {
        window.dataLayer.push(arguments);
      }

      window.gtag = gtag;

      if (localStorage.getItem('cookies_consent') === null) {
        gtag('consent', 'default', {
          ad_storage: 'granted', // google ads
          analytics_storage: 'granted', // google analytics
          personalization_storage: 'granted', // for personalized advertising.
          functionality_storage: 'granted', // languages
          security_storage: 'granted' // authorization or authentication
        });
      } else {
        gtag('consent', 'default', JSON.parse(localStorage.getItem('cookies_consent')));
      }
    </script>
  {/if} -->
</svelte:head>

{#if !dev}
  <noscript>
    <!-- svelte-ignore a11y-missing-attribute -->
    <iframe
      src="https://www.googletagmanager.com/ns.html?id=GTM-PWWWTWCB"
      height="0"
      width="0"
      style="display:none;visibility:hidden"
    />
  </noscript>
{/if}
